import { useEffect, useState } from 'react';

/**
 *
 * @param query 媒体查询字符串，例如默认查询 '(max-width: 576px)'。
 * @returns boolean  表示媒体查询是否匹配。
 */
export const useMediaQuery = (query: string = '(max-width: 576px)'): boolean => {
    const [matches, setMatches] = useState<boolean>(() => window.matchMedia(query).matches);

    useEffect(() => {
        const mediaQueryList = window.matchMedia(query);
        /**
         *
         * @param event MediaQueryListEvent 或 MediaQueryList
         * 兼容说明：
         * 新版浏览器推荐使用 addEventListener/removeEventListener，事件参数为 MediaQueryListEvent，可安全使用 event.matches。
         * 旧版浏览器（如 Safari < 14）只支持 addListener/removeListener，回调参数可能为 MediaQueryList 或 MediaQueryListEvent，没有event.matches，可直接读取 mediaQueryList.matches。
         * 所以统一用 mediaQueryList.matches，确保兼容所有主流浏览器。
         * 参考：
         * https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList/addListener
         * https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList#Browser_compatibility
         *
         * Bugsnag：https://app.bugsnag.com/tigerbrokers/fed-tiger-open-account/errors/6894c960eaede043094599dc?filters[error.status]=open&filters[event.since]=30d&filters[user.id]=4217684523409072&event_id=6894c9ec01420e12e7ec0000
         */
        const handleChange = (event: MediaQueryListEvent | MediaQueryList) => setMatches(mediaQueryList.matches);

        // 设置初始状态
        setMatches(mediaQueryList.matches);

        // 添加监听器，在低版本的浏览器中使用 addListener
        if (mediaQueryList.addEventListener) {
            mediaQueryList.addEventListener('change', handleChange);
        } else if (mediaQueryList.addListener) {
            mediaQueryList.addListener(handleChange);
        }

        // 清理函数
        return () => {
            if (mediaQueryList.removeEventListener) {
                mediaQueryList.removeEventListener('change', handleChange);
            } else if (mediaQueryList.removeListener) {
                mediaQueryList.removeListener(handleChange);
            }
        };
    }, [query]);

    return matches;
};

export default useMediaQuery;
